import React, { useState, useMemo, useCallback } from "react";
import { Search, X, Download, Printer, SlidersHorizontal } from "lucide-react";
import type { Bank, BankAccount, Transaction, VelaReviewSuggestion } from "../types";
import { countByStatus } from "../utils";
import { BANK_TX_TABLE_COLUMNS } from "../constants";
import { AccountSelector } from "./AccountSelector";
import { PeriodPicker } from "./PeriodPicker";
import { VelaBanner } from "./VelaBanner";
import { StatsStrip } from "./StatsStrip";
import { TransactionsTable } from "./TransactionsTable";
import { ReviewDrawer } from "./ReviewDrawer";
import { BankPicker } from "./BankPicker";
import { BankConnectModal } from "./BankConnectModal";
import { printReport } from "../../../../utils/printReport";
import ExportFormatPicker from "../../../../components/common/ExportFormatPicker";
import TableCustomizeDrawer from "../../../../components/common/TableCustomizeDrawer";
import { useTableCustomization } from "../../../../hooks/useTableCustomization";
import { visibleOrderedColumns } from "../../../../components/common/tableCustomization";

interface DashboardProps {
  accounts: BankAccount[];
  transactions: Transaction[];
  reviewSuggestions: VelaReviewSuggestion[];
  onBankConnected: (bank: Bank) => void;
}

export function Dashboard({ accounts, transactions, reviewSuggestions, onBankConnected }: DashboardProps) {
  const [tx, setTx] = useState<Transaction[]>(transactions);
  const [suggestions, setSuggestions] = useState<VelaReviewSuggestion[]>(reviewSuggestions);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState(accounts[0]?.id ?? "");
  const [period, setPeriod] = useState("2026-10");
  const [showPicker, setShowPicker] = useState(false);
  const [pickingBank, setPickingBank] = useState<Bank | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const { customization, draft, updateDraft, applyChanges, cancelDraft, resetToDefault } =
    useTableCustomization("bank-transactions", BANK_TX_TABLE_COLUMNS);

  const scopedTx = useMemo(
    () => tx.filter((t) => t.accountId === selectedAccountId && t.date.startsWith(period)),
    [tx, selectedAccountId, period]
  );

  const filteredTransactions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scopedTx;
    return scopedTx.filter((t) => {
      const payee = (t.cleanDescription || "").toLowerCase();
      const raw = (t.rawDescription || "").toLowerCase();
      const cat = (t.category || "").toLowerCase();
      const amt = String(t.amount);
      return payee.includes(q) || raw.includes(q) || cat.includes(q) || amt.includes(q);
    });
  }, [query, scopedTx]);

  const reviewTransactions = useMemo(
    () => tx.filter((t) => t.status === "review" && suggestions.some((s) => s.transactionId === t.id)),
    [tx, suggestions]
  );
  const velaCount = suggestions.length;
  const autoCount = countByStatus(scopedTx, "auto");

  const handlePrimary = useCallback((txId: string) => {
    const suggestion = suggestions.find((s) => s.transactionId === txId);
    if (!suggestion) return;
    const sourceTx = tx.find((t) => t.id === txId);
    if (!sourceTx) return;

    const applyCategory = suggestion.bulkCategory ?? "Categorized";
    const isBulk = (suggestion.bulkCount ?? 1) > 1;

    setTx((prev) =>
      prev.map((t) => {
        if (isBulk && t.status === "review" && t.cleanDescription === sourceTx.cleanDescription) {
          return { ...t, status: "auto", category: applyCategory };
        }
        if (!isBulk && t.id === txId) {
          return { ...t, status: "auto", category: applyCategory };
        }
        return t;
      })
    );
    setSuggestions((prev) => prev.filter((s) => s.transactionId !== txId));
  }, [suggestions, tx]);

  const handleSkip = useCallback((txId: string) => {
    setSuggestions((prev) => prev.filter((s) => s.transactionId !== txId));
  }, []);

  const handleConnectNew = useCallback(() => setShowPicker(true), []);
  const handleBankSelect = useCallback((bank: Bank) => {
    setShowPicker(false);
    setPickingBank(bank);
  }, []);
  const handleConnected = useCallback((bank: Bank) => {
    setPickingBank(null);
    onBankConnected(bank);
  }, [onBankConnected]);

  const handlePrint = useCallback(() => {
    printReport({ documentTitle: "Bank transactions" });
  }, []);

  // Export honors user customization (only visible columns, in their chosen order)
  const exportOptions = useMemo(() => {
    const visible = visibleOrderedColumns(customization);
    const allExportCols: Record<string, { key: string; label: string; width?: number; align?: "right" }> = {
      date: { key: "date", label: "Date", width: 12 },
      payee: { key: "payee", label: "Payee", width: 32 },
      category: { key: "category", label: "Category", width: 24 },
      amount: { key: "amount", label: "Amount", width: 14, align: "right" },
    };
    return {
      reportName: "Bank transactions",
      columns: visible.map((v) => allExportCols[v.key]).filter(Boolean),
      rows: filteredTransactions.map((t) => ({
        date: t.date,
        payee: t.cleanDescription || t.rawDescription || "",
        category: t.status === "review" ? "Needs review" : (t.category || ""),
        amount: (t.direction === "out" ? -1 : 1) * Math.abs(t.amount),
      })),
    };
  }, [filteredTransactions, customization]);

  const noResults = query.trim() !== "" && filteredTransactions.length === 0;
  const noneInMonth = query.trim() === "" && filteredTransactions.length === 0;

  return (
    <div>
      {/* STICKY TOP */}
      <div className="sticky top-0 z-20 bg-white -mx-4 md:-mx-8 px-4 md:px-8 pt-1 pb-3 border-b border-lineSoft no-print">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center gap-2.5 mb-4">
          <AccountSelector
            accounts={accounts}
            selectedId={selectedAccountId}
            onSelect={setSelectedAccountId}
            onConnectNew={handleConnectNew}
          />
          <PeriodPicker selected={period} onSelect={setPeriod} />
          <div className="md:ml-auto flex items-center gap-2 bg-white border border-line rounded-full px-3.5 py-1.5 md:w-72">
            <Search size={13} strokeWidth={2} className="text-ink4" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, amount, reference..."
              className="flex-1 bg-transparent outline-none text-[12px] placeholder:text-ink4 text-ink"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <IconButton tooltip="Export" onClick={() => setExportOpen(true)}>
              <Download size={14} strokeWidth={2} />
            </IconButton>
            <IconButton tooltip="Print" onClick={handlePrint}>
              <Printer size={14} strokeWidth={2} />
            </IconButton>
            <IconButton tooltip="Customize" onClick={() => setCustomizeOpen(true)}>
              <SlidersHorizontal size={14} strokeWidth={2} />
            </IconButton>
          </div>
        </div>

        <VelaBanner
          reviewCount={velaCount}
          autoCount={autoCount}
          onReviewClick={() => setDrawerOpen(true)}
        />

        <StatsStrip transactions={scopedTx} />
      </div>

      {/* SCROLLING BODY */}
      <div className="print-area pt-4">
        {noResults && (
          <div className="border border-lineSoft rounded-lg py-10 px-6 text-center text-ink3 text-[13px]">
            No transactions match "{query}".
          </div>
        )}
        {noneInMonth && (
          <div className="border border-lineSoft rounded-lg py-10 px-6 text-center text-ink3 text-[13px]">
            No transactions in this period.
          </div>
        )}
        {!noResults && !noneInMonth && (
          <TransactionsTable transactions={filteredTransactions} customization={customization} />
        )}
      </div>

      <ReviewDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        transactions={reviewTransactions}
        suggestions={suggestions}
        onPrimary={handlePrimary}
        onSkip={handleSkip}
      />

      <ExportFormatPicker
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        reportName="Bank transactions"
        options={exportOptions}
      />

      <TableCustomizeDrawer
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        title="Customize"
        columnDefs={BANK_TX_TABLE_COLUMNS}
        draft={draft}
        updateDraft={updateDraft}
        onApply={applyChanges}
        onResetToDefault={resetToDefault}
        onCancel={cancelDraft}
      />

      {/* Connect another bank modal */}
      {showPicker && (
        <div
          onClick={() => setShowPicker(false)}
          className="fixed inset-0 bg-ink/40 z-[1001] flex items-start justify-center p-4 overflow-y-auto"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl max-w-2xl w-full mt-10 p-6 shadow-xl relative"
          >
            <button
              type="button"
              onClick={() => setShowPicker(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-lg text-ink3 hover:bg-lineSoft inline-flex items-center justify-center"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <h2 className="text-lg font-bold text-ink mb-1">Connect another bank</h2>
            <p className="text-[12.5px] text-ink3 mb-5">
              Pick your bank. We use bank-level encryption; your login is never stored.
            </p>
            <BankPicker onBankSelect={handleBankSelect} />
          </div>
        </div>
      )}

      {pickingBank && (
        <BankConnectModal
          bank={pickingBank}
          onClose={() => setPickingBank(null)}
          onConnected={handleConnected}
        />
      )}
    </div>
  );
}

// Reusable icon button with hover tooltip (dark pill, bottom arrow).
function IconButton({ tooltip, onClick, children }: { tooltip: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        className="w-8 h-8 rounded-md bg-white border border-line text-ink3 inline-flex items-center justify-center cursor-pointer hover:bg-[#FAFBFB]"
        aria-label={tooltip}
      >
        {children}
      </button>
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-ink text-white text-[11px] font-semibold px-2 py-1 rounded-md whitespace-nowrap pointer-events-none z-50">
        {tooltip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ink" />
      </span>
    </div>
  );
}